import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { CircleUser } from 'lucide-react';
import { getReservationStats } from '../lib/api';

export default function AppHeader() {
  const [email, setEmail] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBrandUser, setIsBrandUser] = useState(false);
  const [pendingCount, setPendingCount] = useState<number>(0);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/auth/me', { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        setEmail(data.email || null);
        const roles: string[] = data.roles || [];
        const userIsAdmin = roles.includes('admin') || roles.includes('superadmin');
        setIsAdmin(userIsAdmin);
        setIsBrandUser(!!data.brandId);
        
        // Fetch pending count for admin users
        if (userIsAdmin) {
          getReservationStats()
            .then(stats => {
              if (stats && typeof stats.pending === 'number') {
                setPendingCount(stats.pending);
              }
            })
            .catch(err => {
              // Silently fail - don't show errors for this background update
              setPendingCount(0);
            });
        } else {
          setPendingCount(0);
        }
      } catch {}
    })();
  }, [location.pathname]);
  
  useEffect(() => {
    if (!isAdmin) {
      setPendingCount(0);
      return;
    }
    
    // Refresh pending count every 30 seconds for admin users
    const fetchPendingCount = () => {
      getReservationStats()
        .then(stats => {
          if (stats && typeof stats.pending === 'number') {
            setPendingCount(stats.pending);
          }
        })
        .catch(err => {
          // Silently fail - don't show errors for this background update
          setPendingCount(0);
        });
    };
    
    const interval = setInterval(fetchPendingCount, 30000);

    return () => clearInterval(interval);
  }, [isAdmin]);

  async function onLogout() {
    try {
      await fetch('/auth/logout', { method: 'POST' });
    } finally {
      navigate('/login');
    }
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b bg-background">
      <div className="container mx-auto min-h-16 py-2 flex items-center justify-between px-4">
        <Link to="/" className="flex flex-col items-start gap-1">
          <img 
            src="/images/bb_primary logo (outlined).svg" 
            alt="Bright Blue Logo" 
            className="h-8 w-auto"
          />
          <span className="font-semibold text-sm">Bright.Blue Connect</span>
        </Link>
        <nav className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm"><Link to="/">Browse</Link></Button>
          <Button asChild variant="ghost" size="sm"><Link to="/calculator">Campaigns</Link></Button>
          {isBrandUser && !isAdmin && (
            <Button asChild variant="ghost" size="sm"><Link to="/reservations">My Reservations</Link></Button>
          )}
          {isAdmin && (
            <>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/map">Machine Editor</Link></Button>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/users">Users</Link></Button>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/brands">Brands</Link></Button>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/reports">Reports</Link></Button>
              <Button asChild variant="ghost" size="sm" className="relative">
                <Link to="/admin/reservations" className="flex items-center gap-2">
                  Pending Requests
                  {pendingCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full min-w-[20px] h-5 px-1.5 flex items-center justify-center">
                      {pendingCount > 99 ? '99+' : pendingCount}
                    </span>
                  )}
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm"><Link to="/admin/field-options">Settings</Link></Button>
            </>
          )}
        </nav>
        <div className="flex items-center gap-3">
          {email ? (
            <>
              <CircleUser className="w-5 h-5" />
              <span className="text-sm text-muted-foreground hidden sm:inline">{email}</span>
              <Button size="sm" variant="outline" onClick={onLogout}>Logout</Button>
            </>
          ) : (
            <Button asChild size="sm"><Link to="/login">Login</Link></Button>
          )}
        </div>
      </div>
    </header>
  );
}
